export const formatCurrencyString: (unformattedString: string) => string = (unformattedString: string) => {
  return `$${(Number(unformattedString) / 100).toFixed(2).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,')}`;
};

export const formatCurrencyNumberFromString: (formattedString: string) => number = (
  formattedString: string
) => {
  // remove $ from string and convert to number
  return Number(formattedString.replace('$', '').replace(',', ''));
};

export const round = (value: number, precision: number) => {
  const multiplier = Math.pow(10, precision || 0);
  return Math.round(value / multiplier) * multiplier;
};

/**
 * Makes sure the number is good for your math needs. Returns true when input is of type number and is not NaN or Inf.
 */
export const isNumberGoodForMath = (number: unknown): boolean => {
  return typeof number === 'number' && Number.isFinite(number);
};

/**
 * Returns input when it is of type number and is not NaN or Inf., otherwise returns fallback
 */
export const sanitizeNumber = (number: unknown, fallback = 0): number => {
  return isNumberGoodForMath(number) ? (number as number) : fallback;
};

/**
 * Rounds a number up to the next whole dollar when the value is in cents
 * @param valueInCents - The number to round up (in cents)
 * @param fallback - Value returned when valueInCents is not a finite number (default 0)
 * @returns The value rounded up to the next whole dollar (in cents)
 */
export const roundUpToDollarFromCents = (valueInCents: number, fallback = 0): number => {
  if (!isNumberGoodForMath(valueInCents)) return fallback;
  // Convert to dollars, round up, then convert back to cents
  const rounded = Math.ceil(valueInCents / 100) * 100;
  // Normalize -0 (e.g. Math.ceil(-0.5)) to 0
  return rounded === 0 ? 0 : rounded;
};

/**
 * Returns true when a cent value represents a whole-dollar amount (no cents).
 * Non-finite/empty values return true so this rule does not fire on empty or
 * cleared fields — use a separate `required` rule to enforce presence.
 * @param valueInCents - The number to check (in cents)
 */
export const isWholeDollarFromCents = (valueInCents: unknown): boolean => {
  return isNumberGoodForMath(valueInCents) ? (valueInCents as number) % 100 === 0 : true;
};
